import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trash2, Tag, EyeOff, Eye } from 'lucide-react';
import { useCategories } from '@/hooks/useCategories';

interface BulkActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onBulkAction: (action: string, value?: string) => void;
  loading: boolean;
}

export const BulkActionsDialog = ({
  open,
  onOpenChange,
  selectedCount,
  onBulkAction,
  loading,
}: BulkActionsDialogProps) => {
  const [action, setAction] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const { categories, loading: categoriesLoading } = useCategories();

  const handleExecute = () => {
    if (action === 'assign_category') {
      onBulkAction(action, categoryId === 'none' ? undefined : categoryId);
    } else {
      onBulkAction(action);
    }
    
    // Reset state
    setAction('');
    setCategoryId('');
    onOpenChange(false);
  };

  const getActionDescription = () => {
    switch (action) {
      case 'delete':
        return `This will permanently delete ${selectedCount} selected questions. This action cannot be undone.`;
      case 'hide':
        return `This will hide ${selectedCount} selected questions from users. They will not appear in practice sessions or exams.`;
      case 'show':
        return `This will make ${selectedCount} selected questions visible to users again.`;
      case 'assign_category':
        return `This will assign the selected category to ${selectedCount} questions.`;
      default:
        return '';
    }
  };

  const isActionDisabled = () => {
    if (!action) return true;
    if (action === 'assign_category' && (!categoryId || categoryId === 'loading')) return true;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk Actions</DialogTitle>
          <DialogDescription>
            Select an action to perform on {selectedCount} selected questions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="action">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger>
                <SelectValue placeholder="Select an action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delete">
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-destructive" />
                    Delete Questions
                  </div>
                </SelectItem>
                <SelectItem value="hide">
                  <div className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4" />
                    Hide from Users
                  </div>
                </SelectItem>
                <SelectItem value="show">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Show to Users
                  </div>
                </SelectItem>
                <SelectItem value="assign_category">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Assign Category
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {action === 'assign_category' && (
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {categoriesLoading ? (
                    <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                  ) : (
                    categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {action && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                {getActionDescription()}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleExecute}
              disabled={isActionDisabled() || loading}
              className="flex-1"
              variant={action === 'delete' ? 'destructive' : 'default'}
            >
              {loading ? 'Processing...' : 'Execute'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};